import React, { useEffect, useMemo, useState } from "react";
import Loader from "@appComponents/Loader/Loader";
import { TReducers } from "@features/reducers";
import { IWalletDetails } from "@market/interfaces/IWallet";
import {
  Checkbox,
  Col,
  Form,
  Input,
  Row,
  Space,
  Typography,
  Button,
  Divider,
  Switch,
  notification,
} from "antd";
import { useFormik } from "formik";
import { connect } from "react-redux";
import CustomBtn from "../shared/CustomBtn/CustomBtn";
import Big from "big.js";
import * as Yup from "yup";
import SummaryTable, {
  SummaryTableRow,
} from "@appComponents/SummaryTable/SummaryTable";
import CurrencyElement from "@appComponents/CurrencyElement/CurrencyElement";
import FundsService from "@services/Funds.service";
import ReactHtmlParser from "react-html-parser";
import { Link } from "react-router-dom";
import {
  setAccessingFundsAgreementCreatedAt,
  setFundsData,
} from "@funds/features/action.funds";
import ImportantNoticeModal from "@funds/components/ImportantNoticeModal/ImportantNoticeModal";
import CustomSteps from "@funds/components/CustomSteps";
import NoticeIcon from "./danger.svg";
import AlertBox from "@appComponents/AlertBox";
import { validDecimalNumber } from "@bundles/regexps";
import Section from "../shared/Section/Section";
import PercentageBtns from "../shared/PercentageBtns";
import Notice from "../shared/Notice";
import Faq from "../shared/Faq";
import ExpectedGainsChart from "./parts/ExpectedGainsChart";
import NoticeComponent from "@appComponents/Notice/Notice";
import stakingFaq from "./stakingFaq";
import Modal from "@funds/components/Modal/Modal";

import * as style from "./StakeFormStyle.module.scss";
import { appsignal } from "@bundles/appsignal";
import { UnlockBonusesCardContent } from "@appComponents/UnlockBonusesCardContent/UnlockBonusesCardContent";
import { DEFAULT_TIMELOCK_STAKING_MIN_VALUE } from "@bundles/consts";
import { useMediaQuery } from "react-responsive";

type Props = {
  currency: string;
  selectedWallet?: IWalletDetails | undefined;
  history: any;
  setFundsData: (investorId: number) => any;
  companyInvestorId: number | undefined;
  investorId: number;
  accessingFundsAgreementCreatedAt: any;
  setAccessingFundsAgreementCreatedAt: () => void;
  investorSlug: string;
  companySlug: string;
  country?: string;
  userTimelockBalance?: number;
};

const StakeForm = ({
  selectedWallet,
  currency,
  history,
  setFundsData,
  companyInvestorId,
  investorId,
  accessingFundsAgreementCreatedAt,
  setAccessingFundsAgreementCreatedAt,
  investorSlug,
  companySlug,
  country,
  userTimelockBalance,
}: Props) => {
  const isMobile = useMediaQuery({ maxWidth: 768 });

  const genericInvestorId = companyInvestorId || investorId;

  const isUK = useMemo(() => country === "GB", [country]);

  const [step, setStep] = useState(0);
  const [selectedTag, setSelectedTag] = useState("");
  const mainUrl = history.location.pathname.split("/funds/")[0];
  const [isLoading, setIsLoading] = useState(true);
  const [stakedEthApr, setStakedEthApr] = useState(0);
  const [autoRestaking, setAutoRestaking] = useState(false);
  const [validatorDaysToActivate, setValidatorDaysToActivate] =
    useState<string>("Unknown");
  const [modalVisibility, setModalVisibility] = useState(false);
  const [timelockMinValue, setTimelockMinValue] = useState(
    DEFAULT_TIMELOCK_STAKING_MIN_VALUE
  );

  useEffect(() => {
    FundsService.stakedEthApr().then((resp) => {
      setStakedEthApr(resp.data.averaged_reward_rate);

      const hours = resp.data.waiting_time;

      if (hours === "Unknown") {
        setValidatorDaysToActivate("approximately 6 days");
        return;
      }

      const days = Math.floor(hours / 24);
      const remainingHours = hours % 24;
      const daysLabel = days === 1 ? "day" : "days";
      const hoursLabel = remainingHours === 1 ? "hour" : "hours";

      setValidatorDaysToActivate(
        `${days} ${daysLabel} ${
          remainingHours === 0 ? "" : `and ${remainingHours} ${hoursLabel}`
        }`
      );
    });
  }, []);

  const appLink = companyInvestorId
    ? `/companies/${companySlug}/funds`
    : `/profile/${investorSlug}/funds`;

  useEffect(() => {
    Promise.all([
      setFundsData(genericInvestorId),
      FundsService.getAutoStaking(),
      FundsService.getTimelockStakingSettings(),
    ])
      .then(([_, autoRestakingResp, timelockSettingsResp]) => {
        setIsLoading(false);
        setAutoRestaking(autoRestakingResp.data.auto_restaking);
        setTimelockMinValue(
          (prev) =>
            timelockSettingsResp?.data?.timelock_tier_threshold_amount ?? prev
        );
      })
      .catch(() => {
        setIsLoading(true);
      });
  }, []);

  const formik = useFormik({
    initialValues: {
      amount: "",
      agreement1: false,
      agreement2: false,
      agreement3: false,
      agreement4: false,
      agreement5: false,
      timelockFee: null,
      timelockDuration: null,
    },
    validationSchema: Yup.object().shape({
      amount: Yup.string()
        .trim()
        .required("Amount is required")
        .test(
          "validate-min-amount",
          "Please enter an amount greater than or equal to 0.001 ETH, with a maximum of 8 decimal places.",
          (value) => {
            if (!selectedWallet || !value) return true;

            try {
              if (!validDecimalNumber.test(value)) return false;
              return !Big(value).lt(0.001);
            } catch {
              return false;
            }
          }
        )
        .test(
          "validate-amount",
          `Provided value is higher than the amount of ETH in your wallet. <a href="${mainUrl}/funds/manage/buy?currency=eth">Buy ETH</a>`,
          (value) => {
            if (!selectedWallet || !value) return true;
            if (!validDecimalNumber.test(value)) return false;

            return !Big(value).gt(selectedWallet.balance) && !Big(value).eq(0);
          }
        ),
      agreement1: Yup.boolean()
        .required("The terms and conditions must be accepted.")
        .oneOf([true], "The terms and conditions must be accepted."),
      agreement2: Yup.boolean()
        .required("The terms and conditions must be accepted.")
        .oneOf([true], "The terms and conditions must be accepted."),
      agreement3: Yup.boolean()
        .required("The terms and conditions must be accepted.")
        .oneOf([true], "The terms and conditions must be accepted."),
      agreement4: Yup.boolean()
        .required("The terms and conditions must be accepted.")
        .oneOf([true], "The terms and conditions must be accepted."),
      agreement5: Yup.boolean()
        .required("The terms and conditions must be accepted.")
        .oneOf([true], "The terms and conditions must be accepted."),
    }),

    onSubmit: async (values) => {
      try {
        const timelockFee = values.timelockDuration ? values.timelockFee : null;

        await FundsService.postStaking(
          Number(values.amount),
          values.agreement1 &&
            values.agreement2 &&
            values.agreement3 &&
            values.agreement4,
          timelockFee,
          values.timelockDuration,
          selectedTag
        );
        await FundsService.putAutoStaking(autoRestaking);
        history.push(appLink + "/stakingEth");
        notification.success({
          message:
            "Confirmation: You've successfully submitted your staking request.",
        });
      } catch (error) {
        appsignal.sendError(error);
        notification.error({ message: "Staking failed" });
        throw new Error("staking failed");
      }
    },
    validateOnMount: false,
  });

  const handleAmountInputChange = (value: any) => {
    if (!selectedWallet) throw new Error("Wallet not selected");
    if (!validDecimalNumber.test(value.target.value))
      throw new Error("Invalid amount");
    const { balance } = selectedWallet;

    setSelectedTag(
      Big(value.target.value).div(Big(balance)).times(100).toString()
    );
  };

  const handleAmountPercentage = async (amount: number) => {
    if (!selectedWallet) throw new Error("Wallet not selected");
    const { balance } = selectedWallet;
    const result = Big(balance).times(Big(amount).div(100)).round(8).toString();
    await formik.setFieldValue("amount", result, true);
    setSelectedTag(amount.toString());
  };
  if (!selectedWallet || isLoading) return <Loader />;

  if (Big(selectedWallet.balance).lte(0)) {
    return (
      <div className={style.wrapper} style={{ marginBottom: "20px" }}>
        <Row gutter={[0, 4]} justify="space-around" align="top">
          <Col sm={24} md={15}>
            <AlertBox
              message="Note!"
              description={
                <>
                  <Typography.Text style={{ fontWeight: 400 }}>
                    We're sorry, it looks like you don't have any Ethereum in
                    your wallet to use our staking feature. To get started with
                    staking, you will need to purchase some Ethereum.
                  </Typography.Text>
                  <Link
                    className={style.buyEthBtn}
                    to={`${appLink}/manage/buy?currency=eth`}
                  >
                    <Button>Click here to buy Ethereum now.</Button>
                  </Link>
                </>
              }
              type="error"
              showIcon
            />
          </Col>
        </Row>
      </div>
    );
  }

  return (
    <div className={style.wrapper}>
      <Row gutter={[0, 4]} justify="space-around" align="top">
        <Col
          span={24}
          className={style.header}
          style={{ textAlign: "center", margin: "40px auto" }}
        >
          <Typography.Title> Stake Ethereum</Typography.Title>
        </Col>
      </Row>

      <Row gutter={[0, 4]} justify="space-around" align="top">
        <Col sm={24} md={15}>
          <Form className={style.headerWrapperSection}>
            <Section>
              <div className={style.headerWrapper}>
                <CustomSteps
                  current={step}
                  labelPlacement="vertical"
                  items={[
                    {
                      title: "Staking",
                    },
                    {
                      title: "Order Summary",
                    },
                  ]}
                />
              </div>
              <Divider />
              {step === 0 ? (
                <>
                  <Row>
                    <Col className={style.mainLabel}>
                      <Typography.Title level={5}>Stake ETH</Typography.Title>
                    </Col>
                  </Row>

                  <Row>
                    <Col span={24}>
                      <Form.Item
                        name="amount"
                        style={{ marginBottom: "5px" }}
                        help={
                          formik.touched.amount
                            ? ReactHtmlParser(formik.errors.amount ?? "")
                            : false
                        }
                      >
                        <Space
                          direction="vertical"
                          style={{ width: "100%", marginTop: "20px" }}
                        >
                          <Row>
                            <Col span={8}>
                              <label htmlFor="amount" className={style.label}>
                                Enter amount
                              </label>
                            </Col>
                            <Col span={16} style={{ textAlign: "right" }}>
                              <Space
                                style={{
                                  margin: 0,
                                  justifyContent: "flex-end",
                                }}
                                wrap={true}
                              >
                                <b>Available balance:</b>
                                <span className={style.amount}>
                                  {selectedWallet.balance}{" "}
                                  {currency.toLocaleUpperCase()}
                                </span>
                              </Space>
                            </Col>
                          </Row>
                          <div className={style.input}>
                            <Input
                              data-cy="amount-field"
                              {...formik.getFieldProps("amount")}
                              type="tel"
                              onInput={handleAmountInputChange}
                              placeholder={`min. 0.001 - max. ${selectedWallet.balance}`}
                            />
                          </div>
                        </Space>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row justify="space-between">
                    <Col span={24}>
                      <PercentageBtns
                        handleAmountPercentage={handleAmountPercentage}
                        selectedTag={selectedTag}
                      />
                    </Col>
                  </Row>

                  <Row justify="space-between">
                    <Col span={24}>
                      <div className={style.restaking}>
                        <Space
                          align="start"
                          style={{
                            justifyContent: "flex-start",
                          }}
                        >
                          <Switch
                            onChange={(isChecked) => setModalVisibility(true)}
                            checked={autoRestaking}
                          />
                          <div style={{ marginLeft: "10px" }}>
                            <Typography.Title level={5}>
                              Auto-Restaking
                            </Typography.Title>
                            <Typography.Text>
                              {autoRestaking
                                ? `
                              Disabling this feature means your ETH rewards will no longer be
                              automatically reinvested each Monday. You can re-enable
                              Auto-Restaking anytime from the Staking Dashboard if you wish to
                              continue growing your earnings effortlessly.`
                                : `Enable Auto-Restaking to automatically reinvest
                              your ETH rewards received every Monday. Your
                              earnings will be automatically staked without any
                              manual intervention. You can always adjust this
                              setting later from the Staking Dashboard.`}
                            </Typography.Text>
                          </div>
                          <Modal
                            onConfirm={() => {
                              setAutoRestaking(!autoRestaking);
                              setModalVisibility(false);
                            }}
                            open={modalVisibility}
                            onCancel={() => setModalVisibility(false)}
                          >
                            {autoRestaking ? (
                              <>
                                <Typography.Title level={4}>
                                  Are you sure you want to stop Auto-Restaking?
                                </Typography.Title>
                                <Typography.Text>
                                  Disabling this feature means your ETH rewards
                                  will no longer be automatically reinvested
                                  each Monday. You can re-enable Auto-Restaking
                                  anytime from the Staking Dashboard if you wish
                                  to continue growing your earnings
                                  effortlessly.
                                </Typography.Text>
                              </>
                            ) : (
                              <>
                                <Typography.Title level={4}>
                                  Are you sure you want to start Auto-Restaking?
                                </Typography.Title>
                                <Typography.Text>
                                  Enable Auto-Restaking to automatically
                                  reinvest your ETH rewards received every
                                  Monday. Your earnings will be automatically
                                  staked without any manual intervention. You
                                  can always adjust this setting later from the
                                  Staking Dashboard.
                                </Typography.Text>
                              </>
                            )}
                          </Modal>
                        </Space>
                      </div>
                    </Col>
                  </Row>

                  {formik.values.amount && (
                    <>
                      <ExpectedGainsChart
                        formik={formik}
                        isUk={isUK}
                        amount={formik.values.amount}
                        arp={stakedEthApr}
                        timelockMinValue={timelockMinValue}
                        userTimelockBalance={userTimelockBalance}
                        validatorDaysToActivate={validatorDaysToActivate}
                      />

                      <Notice
                        message="Note"
                        icon={<NoticeIcon />}
                        description="Returns are provided in ETH and may fluctuate based on currency exchange rates and the performance of the Ethereum network. The value of returns can increase or decrease accordingly. The figures shown represent expected future performance based on the current rate of return and are not guaranteed. Please note that the performance shown is gross and does not account for commissions, fees, or other charges, which may reduce your actual returns."
                        type="warning"
                        showIcon
                        style={{ margin: "40px auto" }}
                      />
                    </>
                  )}

                  <Notice
                    message="Note"
                    icon={<NoticeIcon />}
                    description="When you choose to stake with us, BF will credit
                    your account with staked ETH along with any applicable staking
                    rewards, in accordance with our Terms and the rules of the Ethereum network.
                    It's important to note that actual rewards are determined by
                    the current rate of the Ethereum network and are subject
                    to fees as outlined in our Fee Schedule."
                    type="warning"
                    showIcon
                    style={{ margin: "40px auto" }}
                  />

                  <NoticeComponent style={{ margin: "20px auto" }} noDivider>
                    <div style={{ padding: "10px 0" }}>
                      <p>
                        <b>Staking Services</b>
                      </p>
                      <p style={{ fontWeight: 400, marginBottom: 0 }}>
                        All staking services are provided by the Virtual Assets
                        Broker based in the British Virgin Islands. The BF
                        Platform Operator nor the BF Securities Broker based in
                        the Cayman Islands do not provide any virtual asset
                        service whatsoever.
                      </p>
                    </div>
                  </NoticeComponent>

                  <Space direction="vertical" style={{ width: "100%" }}>
                    <CustomBtn
                      data-cy="next-btn"
                      myType="submit"
                      onClick={() => {
                        setStep(1);
                        window.scrollTo({
                          top: 0,
                          behavior: "smooth",
                        });
                      }}
                      disabled={
                        formik.isSubmitting ||
                        !formik.dirty ||
                        formik.errors.amount !== undefined
                      }
                      className={style.button}
                    >
                      Next
                    </CustomBtn>

                    <CustomBtn
                      data-cy="cancel-btn"
                      myType="cancel"
                      onClick={() => history.push(appLink)}
                      className={style.button}
                    >
                      Cancel
                    </CustomBtn>
                  </Space>
                </>
              ) : (
                <div style={{ marginTop: "50px" }}>
                  <SummaryTable>
                    <SummaryTableRow
                      label={`Staking Amount Requested`}
                      value={
                        <CurrencyElement
                          value={formik.values.amount}
                          currency={currency}
                        />
                      }
                    />
                    <SummaryTableRow
                      label={`Remaining ETH balance after staking`}
                      value={
                        <CurrencyElement
                          value={Big(selectedWallet.balance)
                            .minus(formik.values.amount)
                            .toString()}
                          currency={currency}
                        />
                      }
                    />

                    <SummaryTableRow
                      label={`Auto-restaking`}
                      value={autoRestaking ? "On" : "Off"}
                    />

                    <SummaryTableRow
                      label={`Timelock staking`}
                      value={
                        (formik.values.timelockDuration as null | number) ===
                        null
                          ? "No timelock"
                          : (
                              formik.values.timelockDuration! as number
                            ).toString() +
                            ` year${
                              (formik.values.timelockDuration! as number) > 1
                                ? "s"
                                : ""
                            } timelock`
                      }
                    />
                  </SummaryTable>

                  <Notice
                    message="Note!"
                    icon={<NoticeIcon />}
                    description={`The current estimated waiting time for a validator to become active and start earning rewards is ${validatorDaysToActivate}.`}
                    type="warning"
                    showIcon
                    style={{ margin: "40px auto" }}
                  />
                  <div className={style.agreementsWrapper}>
                    <Form.Item
                      required
                      hasFeedback
                      help={
                        formik.touched.agreement4
                          ? formik.errors.agreement4
                          : false
                      }
                      className={style.label}
                    >
                      <div className={style.checkboxWraper}>
                        <Checkbox
                          data-cy="agreement4"
                          {...formik.getFieldProps("agreement4")}
                          checked={formik.values.agreement4}
                          id="agreement4"
                        >
                          <Typography.Text>
                            <label htmlFor="agreement4" className={style.label}>
                              <b>Staking Order Submission:</b>&nbsp;I
                              acknowledge that I am submitting an order for the
                              staking of my Ether (ETH). I understand that this
                              order will be queued and the order will be
                              accepted by BF Virtual Assets Ltd. conditional
                              upon achieving the requisite Minimum Threshold for
                              staking.
                            </label>
                          </Typography.Text>
                        </Checkbox>
                      </div>
                    </Form.Item>

                    <Form.Item
                      required
                      hasFeedback
                      help={
                        formik.touched.agreement1
                          ? formik.errors.agreement1
                          : false
                      }
                      className={style.label}
                    >
                      <div className={style.checkboxWraper}>
                        <Checkbox
                          data-cy="agreement1"
                          {...formik.getFieldProps("agreement1")}
                          checked={formik.values.agreement1}
                          id="agreement1"
                        >
                          <Typography.Text>
                            <label htmlFor="agreement1" className={style.label}>
                              <b>Fee Schedule and Terms Acceptance:</b>&nbsp;I
                              understand and agree to the fees outlined in the{" "}
                              <a
                                href="https://bnktothefuture.com/fees"
                                target="_blank"
                              >
                                Fee Schedule
                              </a>{" "}
                              and the terms of the{" "}
                              <a
                                href="/terms_and_conditions/staking"
                                target="_blank"
                              >
                                BF Staking Terms
                              </a>
                              , including those of Figment. I am aware of the
                              maximum fee of 20% on staking rewards, the
                              potential for a 1% fee reduction for each year of
                              time lock commitment, and the potential risks
                              associated with staking. I agree to the fees
                              outlined in the Fee Schedule, which includes a
                              maximum 20% fee on rewards with discounts for
                              longer time locks.
                            </label>
                          </Typography.Text>
                        </Checkbox>
                      </div>
                    </Form.Item>
                    <Form.Item
                      required
                      hasFeedback
                      help={
                        formik.touched.agreement2
                          ? formik.errors.agreement2
                          : false
                      }
                      className={style.label}
                    >
                      <div className={style.checkboxWraper}>
                        <Checkbox
                          data-cy="agreement2"
                          {...formik.getFieldProps("agreement2")}
                          checked={formik.values.agreement2}
                          id="agreement2"
                        >
                          <Typography.Text>
                            <label htmlFor="agreement2" className={style.label}>
                              <b>Risks Understanding:</b>&nbsp;I understand and
                              acknowledge the potential risks associated with
                              staking Ethereum, including technical issues,
                              value fluctuations, hard forks, smart contract
                              vulnerabilities, and the impact these factors
                              could have on the value of my staked funds.
                            </label>
                          </Typography.Text>
                        </Checkbox>
                      </div>
                    </Form.Item>
                    <Form.Item
                      required
                      hasFeedback
                      help={
                        formik.touched.agreement3
                          ? formik.errors.agreement3
                          : false
                      }
                      className={style.label}
                    >
                      <div className={style.checkboxWraper}>
                        <Checkbox
                          data-cy="agreement3"
                          {...formik.getFieldProps("agreement3")}
                          checked={formik.values.agreement3}
                          id="agreement3"
                        >
                          <Typography.Text>
                            <label htmlFor="agreement3" className={style.label}>
                              <b>Time-Locked ETH:</b>&nbsp;I understand that if
                              I have chosen the Time-Locked Staking Service, my
                              ETH will be locked for an extended period, making
                              it inaccessible for withdrawal. By agreeing to a
                              time-lock, I confirm that I will not be able to
                              cancel or withdraw my staked ETH during this time.
                            </label>
                          </Typography.Text>
                        </Checkbox>
                      </div>
                    </Form.Item>

                    <Form.Item
                      required
                      hasFeedback
                      help={
                        formik.touched.agreement5
                          ? formik.errors.agreement5
                          : false
                      }
                      className={style.label}
                    >
                      <div className={style.checkboxWraper}>
                        <Checkbox
                          data-cy="agreement5"
                          {...formik.getFieldProps("agreement5")}
                          checked={formik.values.agreement5}
                          id="agreement5"
                        >
                          <Typography.Text>
                            <label htmlFor="agreement5" className={style.label}>
                              <b>Rewards Disclaimer:</b>&nbsp;I understand that
                              staking rewards are not guaranteed and may
                              fluctuate. The provided APR is an estimate and may
                              vary based on network conditions and the number of
                              new validators.
                            </label>
                          </Typography.Text>
                        </Checkbox>
                      </div>
                    </Form.Item>
                  </div>

                  <Space direction="vertical" style={{ width: "100%" }}>
                    <CustomBtn
                      data-cy="confirm-btn"
                      myType="submit"
                      onClick={formik.submitForm}
                      loading={formik.isSubmitting}
                      disabled={
                        !formik.dirty ||
                        !formik.isValid ||
                        formik.isSubmitting ||
                        !formik.dirty ||
                        !formik.isValid
                      }
                      className={style.button}
                    >
                      Confirm Order
                    </CustomBtn>

                    <CustomBtn
                      data-cy="cancel-btn"
                      myType="cancel"
                      onClick={() => {
                        setStep(0);
                        window.scrollTo({
                          top: 0,
                          behavior: "smooth",
                        });
                      }}
                      className={style.button}
                    >
                      Cancel
                    </CustomBtn>
                  </Space>
                </div>
              )}
            </Section>
          </Form>
        </Col>

        <Col sm={24} md={7}>
          <Faq faq={stakingFaq} />

          {!isMobile && (
            <div className="mt-8">
              <Section>
                <p className="text-xs uppercase mb-2 font-bold">
                  {isUK ? "Features" : "Bonuses"}
                </p>
                <UnlockBonusesCardContent
                  isUk={isUK}
                  timelockMinValue={timelockMinValue}
                  timeLockBalance={userTimelockBalance}
                />
              </Section>
            </div>
          )}
        </Col>
      </Row>
      {!accessingFundsAgreementCreatedAt && (
        <ImportantNoticeModal
          setAccessingFundsAgreementCreatedAt={
            setAccessingFundsAgreementCreatedAt
          }
        />
      )}
    </div>
  );
};

const mapStateToProps = (store: TReducers, ownProps: any) => ({
  investorId: store.userInfo.investor_id,
  companyInvestorId: store.companyReducer.companyDetails.investor_id,
  accessingFundsAgreementCreatedAt:
    store.userInfo.accessing_funds_agreement_created_at,
  selectedWallet: store.fundsReducer.supportedUserWallets?.find(
    (wallet) => wallet.currency === ownProps.currency
  ),
  investorSlug: store.userInfo.nickname,
  country: store.userInfo.country,
  userTimelockBalance: store.userInfo.timelock_balance,
  companySlug: store.companyReducer.companyDetails.slug,
});

const mapDispatchToProps = (dispatch: Function) => ({
  setFundsData: (investorId: number) => dispatch(setFundsData(investorId)),
  setAccessingFundsAgreementCreatedAt: () =>
    dispatch(setAccessingFundsAgreementCreatedAt()),
});

export default connect(mapStateToProps, mapDispatchToProps)(StakeForm);
